<template>
  <v-dialog persistent scrollable width="650" :value="show">
    <v-card>
      <v-card-title
        class="ma-0 px-6 py-4 flex-column align-start"
        data-testid="routineApprovalHistoryDialog-title"
      >
        <p class="ma-0 pa-0 h6 font-weight-600">
          {{ $t('routines.approval.history_title') }}
        </p>
      </v-card-title>

      <v-card-text class="pt-0 px-4 pb-4">
        <EmptyState
          v-if="isEmptyHistory"
          icon="mdi-history"
          :message="$t('routines.approval.history_empty_message')"
          :title="$t('routines.approval.history_empty_title')"
          data-testid="routineApprovalHistoryDialog-emptyState"
        />

        <v-stepper
          v-else
          vertical
          flat
          class="routine-approval-history"
          data-testid="routineApprovalHistoryDialog-stepper"
        >
          <v-stepper-step
            v-for="(entry, index) in sortedHistory"
            :key="`${entry.updatedAt}_${index}`"
            :step="index + 1"
            :complete="true"
            :complete-icon="historyStatusVisual(entry.status).icon"
            :color="historyStatusVisual(entry.status).color"
            :class="`routine-approval-history__step--${entry.status}`"
            :data-testid="`routineApprovalHistoryDialog-step-${index}`"
          >
            <div
              class="routine-approval-history__step-body"
              :data-testid="`routineApprovalHistoryDialog-stepBody-${index}`"
            >
              <div
                class="d-flex align-center"
                :data-testid="
                  `routineApprovalHistoryDialog-status-${entry.status}`
                "
              >
                <span
                  class="body-2 blue-grey--text text--darken-3 font-weight-medium"
                >
                  {{ $t(historyStatusVisual(entry.status).i18nKey) }}
                </span>
              </div>

              <p
                class="caption blue-grey--text ma-0 pa-0 mt-1"
                :data-testid="`routineApprovalHistoryDialog-date-${index}`"
              >
                {{ formatHistoryDate(entry.updatedAt) }}
              </p>

              <div
                v-if="resolveActor(entry)"
                class="mt-2"
                :data-testid="`routineApprovalHistoryDialog-actor-${index}`"
              >
                <ChipAvatar small :account="resolveActor(entry)" />
              </div>

              <div
                v-if="
                  entry.status === APPROVAL_STATUS.REJECTED &&
                    entry.rejectionReason
                "
                class="routine-approval-history__rejection mt-5"
                :data-testid="
                  `routineApprovalHistoryDialog-rejectionReason-${index}`
                "
              >
                <p
                  class="caption blue-grey--text text--darken-3 ma-0 pa-0 font-weight-medium"
                >
                  {{ $t('routines.approval.rejection_reason') }}
                </p>
                <p
                  class="body-2 blue-grey--text text--darken-1 ma-0 pa-0 px-2 mt-1"
                  style="font-style: italic;"
                >
                  "{{ entry.rejectionReason }}"
                </p>
              </div>
            </div>
          </v-stepper-step>
        </v-stepper>
      </v-card-text>

      <v-card-actions class="justify-end px-4 pb-4">
        <v-btn
          color="blue-grey darken-1"
          rounded
          text
          data-testid="routineApprovalHistoryDialog-button-close"
          @click="$emit('close')"
        >
          {{ $t('common.button.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import ChipAvatar from '@/components/ChipAvatar.vue'
import EmptyState from '@/components/EmptyState.vue'
import { APPROVAL_STATUS } from '@/containers/RoutineExecutionView/Approvals/constants'

const HISTORY_STATUS_VISUALS = Object.freeze({
  [APPROVAL_STATUS.REQUESTED]: {
    icon: 'mdi-clock-outline',
    color: 'blue lighten-2',
    i18nKey: 'routines.approval.request_status_requested',
  },
  [APPROVAL_STATUS.APPROVED]: {
    icon: 'mdi-check',
    color: 'green lighten-2',
    i18nKey: 'routines.approval.approved',
  },
  [APPROVAL_STATUS.REJECTED]: {
    icon: 'mdi-close',
    color: 'red lighten-2',
    i18nKey: 'routines.approval.rejected',
  },
})

export default {
  name: 'RoutineApprovalHistoryDialog',

  components: {
    ChipAvatar,
    EmptyState,
  },

  props: {
    show: Boolean,
    history: {
      type: Array,
      default: () => [],
    },
    approval: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      APPROVAL_STATUS,
    }
  },

  computed: {
    sortedHistory() {
      return _.orderBy([...(this.history || [])], ['updatedAt'], ['desc'])
    },

    isEmptyHistory() {
      return _.isEmpty(this.sortedHistory)
    },
  },

  methods: {
    historyStatusVisual(status) {
      return (
        HISTORY_STATUS_VISUALS[status] || {
          icon: 'mdi-help-circle-outline',
          color: 'blue-grey',
          i18nKey: 'routines.approval.unknown_status',
        }
      )
    },

    formatHistoryDate(value) {
      if (!value) return ''
      return utils.dates.formatDate(value, 'MMM D YYYY, h:mm A')
    },

    resolveActor(entry) {
      if (_.isPlainObject(entry?.updatedBy)) return entry.updatedBy

      if (entry?.status === APPROVAL_STATUS.REQUESTED) {
        return _.isPlainObject(this.approval?.createdBy)
          ? this.approval.createdBy
          : null
      }

      if (
        entry?.status === APPROVAL_STATUS.APPROVED ||
        entry?.status === APPROVAL_STATUS.REJECTED
      ) {
        return _.isPlainObject(this.approval?.approver)
          ? this.approval.approver
          : null
      }

      return null
    },
  },
}
</script>

<style lang="scss">
.routine-approval-history {
  padding-bottom: 0 !important;

  /* Custom connector line between vertical stepper circles.
     Vuetify's vertical stepper draws the line as a left border on
     v-stepper__content panels. Since this dialog renders bodies inside
     v-stepper-step slots (one body always visible per entry), the
     line is replicated here on each step wrapper except the last one. */
  & .v-stepper__step {
    position: relative;
    align-items: start !important;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 56px;
      bottom: -18px;
      left: 36px;
      width: 1px;
      background: rgba(0, 0, 0, 0.12);
    }
  }

  /* Per-status icon glyph color inside the stepper circle.
     Mirrors the chip-icon color used in ApprovalItem.vue:
     approved = green darken-1, rejected = red darken-1, requested = blue darken-1. */
  &__step--approved .v-stepper__step__step i {
    color: $color-green !important;
  }

  &__step--rejected .v-stepper__step__step i {
    color: $color-red !important;
  }

  &__step--requested .v-stepper__step__step i {
    color: $color-blue !important;
  }
}
</style>
